// src/components/Filter/FilterModuleCheckbox.jsx
import { useConfig } from '../../contexts/ConfigContext';
import { useAppState } from '../../contexts/AppStateContext';
import { filterTypes } from '../../config';
import { toLowerWithDashes, formatCategory, customStartCase } from '../../util';

const FilterModuleCheckbox = ({
    filterType,
    filters,
    selectedFilters,
    handleChange,
    mobile = false,
    title = null,
}) => {
    const { retailerId } = useAppState();
    const { tagSlugs } = useConfig();

    function getTagDisplayText(filter) {
        if (
            filterType === filterTypes.tags &&
            tagSlugs &&
            tagSlugs?.hasOwnProperty(retailerId)
        ) {
            return tagSlugs[retailerId]?.hasOwnProperty(filter)
                ? tagSlugs[retailerId][filter]
                : filter;
        }
        return filter;
    }

    const isChecked = (filter) => {
        if (!selectedFilters || !selectedFilters[filterType]) {
            return false;
        }
    
        if (filter === filterTypes.specials) {
            return !!selectedFilters[filterTypes.specials];
        }
        if (filter === filterTypes.staff_picks) {
            return !!selectedFilters[filterTypes.staff_picks];
        }
    
        const selectedFilter = selectedFilters[filterType];
    
        if (Array.isArray(selectedFilter)) {
            if (filterType === filterTypes.tags) {
                // For tags, check the direct value without transformation
                // since we store the raw value in selectedFilters
                return selectedFilter.includes(filter);
            } else if (filterType === 'category') {
                return selectedFilter.includes(typeof filter === 'string' ? filter : filter.headless_categories_to_show_menu_name);
            } else if (filterType === filterTypes.brands) {
                // For brands, check by ID
                return selectedFilter.includes(filter?.id || filter);
            } else {
                // For other multi-select filters like weights, effects, subcategory, strain
                return selectedFilter.includes(filter);
            }
        } else {
            if (filterType === 'category') {
                return selectedFilter === (typeof filter === 'string' ? filter : filter.headless_categories_to_show_menu_name);
            } else if (filterType === 'subcategory' || filterType === 'strain') {
                // For subcategory and strain that might be transitioning between string and array
                return selectedFilter === filter;
            }
            return selectedFilter === filter || selectedFilter === filter?.id;
        }
    };

    return (
        <div className='filter-checkbox-container'>
            {mobile && (
                <div className='filter-sidebar-header'>
                    <div className='filter-sidebar-title'>{title}</div>
                </div>
            )}
            <div className='filter-sidebar-options filter-sidebar-options-checkboxes'>
                {Array.isArray(filters) &&
                    filters.map((filter) => {
                        // Handle both string and object filters for categories
                        const isStringFilter = typeof filter === 'string';
                        const isObjectFilter = typeof filter === 'object' && filter !== null;

                        // Skip filters based on type
                        if (filterType === 'category') {
                            if (isStringFilter && (filter === 'NOT_APPLICABLE' || filter === 'DEFAULT')) {
                                return null;
                            }
                            if (isObjectFilter && !filter.headless_categories_to_show_menu_show) {
                                return null;
                            }
                        }

                        if (filterType === 'subcategory' && filter === 'DEFAULT') {
                            return null;
                        }

                        if (isStringFilter &&
                            (filter.toLowerCase() === 'not_applicable' ||
                                filter.toLowerCase() === 'n/a')) {
                            return null;
                        }

                        // Extract filter value based on type
                        let filterValue;
                        if (filterType === 'category' && isObjectFilter) {
                            filterValue = filter.headless_categories_to_show_menu_name;
                        } else if (isObjectFilter) {
                            filterValue = filter.id || filter.name || filter;
                        } else {
                            filterValue = filter;
                        }

                        // Extract display text
                        const filterDisplay = (() => {
                            if (filterType === 'category' && isObjectFilter) {
                                return formatCategory(filter.headless_categories_to_show_menu_name);
                            } else if (isStringFilter) {
                                return (filterType === 'subcategory' || filterType === 'effects')
                                    ? customStartCase(filter)
                                    : formatCategory(filter);
                            } else {
                                return customStartCase(filter?.name || filter);
                            }
                        })();

                        const displayText = filterType === filterTypes.weights
                            ? filter
                            : filterType === filterTypes.tags
                                ? getTagDisplayText(filter)
                                : filterDisplay;

                        const checkboxId = `headless_category_${filterValue}`;

                        return (
                            <div
                                key={filterValue}
                                className='filter-option filter-option-checkbox'
                            >
                                <input
                                    type='checkbox'
                                    id={checkboxId}
                                    className='headless_filter_checkbox'
                                    checked={isChecked(filter)}
                                    onChange={(e) => handleChange(e.target, filterType)}
                                    value={filterValue}
                                    name={checkboxId}
                                    aria-label={`Filter by ${displayText}`}
                                />
                                <label
                                    htmlFor={checkboxId}
                                    className='checkbox-label'
                                >
                                    {displayText}
                                </label>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default FilterModuleCheckbox;
